import { useCallback, useEffect, useRef, useState } from 'react'
import { faTimes } from '@fortawesome/pro-light-svg-icons'
import { BuyModelTypeExtended, OnChangeSearchBarAddBuySupplier } from './types'
import useTwinTranslation from '../../../utils/hooks/useTwinTranslation'
import CustomSelect from '../../../forms/CustomSelect'
import Checkbox from '../../../forms/Checkbox'
import SearchBar from '../../../baseComponents/SearchBar'
import SearchBarBuySupplierResults from './SearchBuySupplierResults'
import { twinFetchPostJSON } from '../../../utils/globals/data'
import useOnClickOutside from '../../../utils/hooks/useOnClickOut'

interface SearchbarBuySupplierDeciderProps {
    translations: {
        title: string
        seeText: string
        duplicateText: string
    }
    baseURL: string
    getDataURL: string
}

const SearchbarBuySupplierDecider: React.FC<SearchbarBuySupplierDeciderProps> = ({ baseURL, getDataURL, translations }: SearchbarBuySupplierDeciderProps) => {
    const { data, setData } = useSearchbarBuySupplierDeciderLogic()
    const { t } = useTwinTranslation()
    const oldURL = baseURL + '/' + String(data?.id)
    if (data?.id) {
        const nameValue = data?.full_assigned_number + ' - ' + data?.Supplier?.name
        return (
            <div className='flex flex-col'>
                <CustomSelect name='OrderId' value={data.id} icon={faTimes} readOnly={true} label={translations.title} items={{ [data.id]: { name: nameValue } }} onClickIcon={() => setData(null)} className='mt-16' />
                <a href={oldURL} rel="noreferrer"  target='_blank' className='cursor-pointer text-green-43 ml-auto mt-10'>{translations.seeText}</a>
                <Checkbox className='mt-20 block' name='updatePrice' label={t('updatePricesOfProducts', 'Actualizar precios de los productos')} defaultChecked={true}></Checkbox>
                <Checkbox className='mt-20 block' name='updateCopyProduct' label={t('updateNameAndDescriptions', 'Actualizar nombre y descripción de los productos')} defaultChecked={true}></Checkbox>
                <Checkbox className='mt-20 block' name='copyNotes' label={t('copyNotes', 'Copiar notas')} defaultChecked={true}></Checkbox>
            </div>
        )
    }
    return (
        <SearchBarBuySupplier className='mt-16' placeholder={translations.duplicateText} setData={setData} getDataURL={getDataURL} />
    )
}

const useSearchbarBuySupplierDeciderLogic = () => {
    const [data, setData] = useState<BuyModelTypeExtended | null>(null)
    return { setData, data}
}

interface SearchBarBuySupplierProps extends SearchBarBuySupplierLogicProps {
    title?: string
    className?: string
    placeholder?: string
}

const SearchBarBuySupplier: React.FC<SearchBarBuySupplierProps> = ({ title, className, placeholder, ...rest }) => {
    const { setSearchValue, items, searchValue, ref, myChangeSelectedItems } = useSearchBarBuySupplierLogic({ ...rest })
    return (
        <div className={'relative ' + (className || '')} ref={ref}>
            {title ? <span className='block regular18 my-22'>
                {title}
            </span> : null}
            <SearchBar onChange={setSearchValue} {...{ searchValue, placeholder }} />
            {items && <SearchBarBuySupplierResults buys={items} onSelectBuy={myChangeSelectedItems} className='w-full' />}
        </div>
    )
}

interface SearchBarBuySupplierLogicProps {
    getDataURL: string
    setData: React.Dispatch<React.SetStateAction<BuyModelTypeExtended | null >>
}

const useSearchBarBuySupplierLogic = ({ setData, getDataURL }: SearchBarBuySupplierLogicProps) => {
    const ref = useRef<any>(null)
    const [searchValue, setSearchValue] = useState<string>('')
    const [items, setItems] = useState<BuyModelTypeExtended[] | null>(null)

    const resetSearchValue = useCallback(() => {
        setSearchValue('')
        setItems(null)
    }, [setSearchValue])

    const myChangeSelectedItems: OnChangeSearchBarAddBuySupplier = useCallback((value) => {
        setData(value)
        resetSearchValue()
    }, [setData, resetSearchValue])

    const searchItems = useCallback(async (value: string) => {
        const searchedItems = await twinFetchPostJSON(getDataURL, {
            needPrices: true,
            where: {
                '$or': {
                    'full_assigned_number': {
                        '$iLike': `%${value}%`
                    },
                    'Supplier.name': { '$iLike': `%${value}%` },
                }
            },
            limit: 15
        })
        setItems(searchedItems)
    }, [setItems, getDataURL])


    useOnClickOutside(ref, () => resetSearchValue())


    useEffect(() => {
        if (searchValue !== '') {
            searchItems(searchValue)
        } else {
            setItems(null)
        }
    }, [searchValue, searchItems])

    return { setSearchValue, items, searchValue, resetSearchValue, ref, myChangeSelectedItems }
}

export default SearchbarBuySupplierDecider